import glob_const from './glob_const'
import type { Customer, CountriesRecord } from '../types'

/**
 * Niveau de correspondance du texte de recherche avec la clé (shortname) du client.
 * Texte et clé sont normalisés de la même façon : espaces de bord retirés, espaces multiples réduits, majuscules.
 * À partir de 2 caractères : `exact` si la clé est égale au texte, `contains` si elle le contient, sinon `null`.
 */
export function customerKeyMatch(customer: Customer, searchText: string): 'exact' | 'contains' | null {
   const text = (searchText ?? '').trim().replace(/\s+/g, ' ').toUpperCase()
   const key = customer.shortname?.toUpperCase().trim().replace(/\s+/g, ' ')
   if (text.length < 2 || !key) {
      return null
   }
   if (key === text) {
      return 'exact'
   }
   return key.includes(text) ? 'contains' : null
}

/**
 * Tri stable par pertinence de la clé : clients `exact`, puis `contains`, puis les autres (ordre d'origine
 * conservé dans chaque niveau). Ne mute pas le tableau reçu ; sans effet pour un texte de moins de 2 caractères.
 */
export function sortCustomersByKeyRelevance(customers: Customer[], searchText: string): Customer[] {
   const rank = (c: Customer) => {
      const k = customerKeyMatch(c, searchText)
      return k === 'exact' ? 0 : k === 'contains' ? 1 : 2
   }
   return customers.map((c, i) => ({ c, i, r: rank(c) })).sort((a, b) => a.r - b.r || a.i - b.i).map((x) => x.c)
}

/**
 * Un client correspond-il au texte de recherche ? Prospects et KTM sont toujours exclus ; insensible à la casse.
 * Il correspond si (A) ou (B) :
 * - (A) recherche par mots : termes séparés par des espaces = OU (termes vides ignorés) ; sous-termes reliés
 *   par `+` = ET. Un sous-terme est cherché dans le code, le nom, le pays (« contient ») et dans la ville du
 *   siège (un des mots de la ville, séparés par espace, tiret ou apostrophe, commence par le sous-terme).
 *   La clé n'y participe pas ;
 * - (B) recherche par clé : `customerKeyMatch` non nul (texte entier, `+` compris, à partir de 2 caractères,
 *   égal à la clé ou contenu dans la clé).
 * `countryLabel` : libellé du pays du siège dans la langue courante (sinon le code pays est utilisé).
 */
export function matchesCustomerSearch(customer: Customer, searchText: string, countryLabel?: string | null): boolean {
   const id = customer.id
   if (id && (id.startsWith(glob_const.prefix_prospect) || id.startsWith(glob_const.prefix_ktm))) {
      return false
   }
   const text = (searchText ?? '').trim().replace(/\s+/g, ' ').toUpperCase()
   const name = customer.name?.toUpperCase()
   const city = (customer.addresses?.SIEGE?.city as string | undefined)?.toUpperCase()
   const cityWords = city?.split(/[\s'’-]+/) ?? []
   const country = (countryLabel ?? customer.addresses?.SIEGE?.country)?.toUpperCase()

   if (customerKeyMatch(customer, searchText)) {
      return true
   }
   return text.split(' ').some((str) => {
      const subs = str.split('+').filter(Boolean)
      return subs.length > 0 && subs.every((sub) => {
         return !!(name?.includes(sub) || id?.includes(sub) || country?.includes(sub) || cityWords.some((w) => w.startsWith(sub)))
      })
   })
}

/** Libellé du pays du siège selon la langue, avec repli sur le code. */
export function customerCountryLabel(customer: Customer, countries: CountriesRecord | undefined, lang: string): string | null {
   const country_id = customer?.addresses?.SIEGE?.country?.toUpperCase() ?? null
   if (country_id && countries && countries[country_id]) {
      return countries[country_id][lang] ?? country_id
   }
   return country_id
}
